import {
  MockAPIRouter,
  MockInterceptorEntry,
  RequestInterceptorEntry,
  ResponseInterceptorEntry,
} from '@frontend/fetch';
import { FeatureMockRouter } from './types';

const apiEndpointCache = new Map<SchemaService[keyof SchemaService], Promise<{ method: string; url?: string }>>();

export type SchemaMethod = (fn: (url: string, reqInit: RequestInit) => Promise<any>, req: any) => ReturnType<typeof fn>;
export type SchemaService = {
  [P: string]: SchemaMethod;
};

/**
 * Extracts API endpoint information from a SchemaService endpoint function for demo/mock interceptor purposes.
 * Uses promise caching to prevent duplicate computations when called simultaneously for the same schema method.
 *
 * @param schemaMethod - A function from SchemaService that defines an API endpoint
 * @returns Promise containing the HTTP method and base URL of the endpoint
 *   - method: The HTTP method (defaults to 'GET' if not specified)
 *   - url: The endpoint URL without query parameters
 *
 * @example
 * const endpoint = await getApiEndpointForDemoInterceptor(schemaService.getUserProfile);
 * // Returns: { method: 'GET', url: '/api/users/profile' }
 */
const getApiEndpointForDemoInterceptor = async (
  schemaMethod: SchemaService[keyof SchemaService]
): Promise<{ method: string; url?: string }> => {
  const cachedPromise = apiEndpointCache.get(schemaMethod);
  if (cachedPromise) {
    return cachedPromise;
  }

  const promise = new Promise<{ method: string; url?: string }>((resolve, reject) => {
    try {
      const placeholderRequest = new Proxy(
        {},
        {
          get: (_target, prop) => `:${String(prop)}`,
        }
      );

      schemaMethod(async (endpointUrl, { method = 'GET' }) => {
        resolve({ method, url: endpointUrl.split('?')[0] });
      }, placeholderRequest);
    } catch (error) {
      // Remove from cache on error so it can be retried
      apiEndpointCache.delete(schemaMethod);
      reject(error);
    }
  });

  apiEndpointCache.set(schemaMethod, promise);

  return promise;
};

/**
 * Checks if a request URL and method matches a schema endpoint's configuration.
 *
 * @param params - The parameters object
 * @param params.schemaMethod - The schema service method to compare against
 * @param params.requestUrl - The URL of the request to check
 * @param params.method - Optional HTTP method of the request (GET, POST, etc)
 * @returns Promise<boolean> - True if the request matches the schema endpoint
 */
const isMatchingApiEndpoint = async ({
  schemaMethod,
  requestUrl,
  method,
}: {
  schemaMethod: SchemaService[keyof SchemaService];
  requestUrl: string;
  method?: Request['method'];
}): Promise<boolean> => {
  try {
    const schemaEndpoint = await getApiEndpointForDemoInterceptor(schemaMethod);
    const pathname = requestUrl.startsWith('http') ? new URL(requestUrl).pathname : requestUrl;

    return pathname === schemaEndpoint.url && (!method || method.toUpperCase() === schemaEndpoint.method.toUpperCase());
  } catch (error) {
    console.warn('Error matching API endpoint:', error);
    return false;
  }
};

const getRequestInterceptors = <Ctx>(
  interceptorRouters: FeatureMockRouter<Ctx>[],
  context: Ctx
): RequestInterceptorEntry[] => {
  return interceptorRouters.flatMap((router) => router.getRequestInterceptors?.(context) || []);
};

const getResponseInterceptors = <Ctx>(
  interceptorRouters: FeatureMockRouter<Ctx>[],
  context: Ctx
): ResponseInterceptorEntry[] => {
  return interceptorRouters.flatMap((router) => router.getResponseInterceptors?.(context) || []);
};

const getMockRouter = <Ctx>(
  interceptorRouters: FeatureMockRouter<Ctx>[],
  context: Ctx,
  interceptorIdPrefix: string
): MockInterceptorEntry => {
  const mockApiRouter = new MockAPIRouter();
  const mockInterceptorId = `${interceptorIdPrefix}-mock-interceptor`;

  interceptorRouters.forEach((router) => {
    router.addMockRouterEndpoints?.(mockApiRouter, context);
  });

  return {
    id: mockInterceptorId,
    mockApiRouter,
  };
};

export const DemoInterceptorUtils = {
  getApiEndpointForDemoInterceptor,
  isMatchingApiEndpoint,
  getRequestInterceptors,
  getResponseInterceptors,
  getMockRouter,
};
